import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MagnifyingGlass } from '@phosphor-icons/react';
import { Dialog } from '@radix-ui/react-dialog';

import { DialogContent, DialogOverlay, DialogPortal } from '@/ui/Dialog';
import { cn } from '@/utils/cn';

import * as styles from './RSSFeedSearchModal.css';
import type { RSSFeedSearchModalElementProps, RSSFeedSearchResult } from './RSSFeedSearchModal.types';

interface RSSFeedSearchResultItemProps {
  result: RSSFeedSearchResult;
  isSelected: boolean;
  onMouseEnter: () => void;
}

function RSSFeedSearchResultItem({ result, isSelected, onMouseEnter }: RSSFeedSearchResultItemProps) {
  const itemRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isSelected && itemRef.current) {
      itemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  return (
    <a
      ref={itemRef}
      href={result.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.searchResultLink}
      onMouseEnter={onMouseEnter}
      data-selected={isSelected}
      role="option"
      aria-selected={isSelected}
    >
      <div className={cn(styles.searchResult, isSelected && styles.searchResultSelected)}>
        {result.imageUrl && (
          <img src={result.imageUrl} alt={result.title} className={styles.searchResultImage} loading="lazy" />
        )}
        <div className={styles.searchResultContent}>
          <div className={styles.searchResultTitle}>{result.title}</div>
          {result.description && <div className={styles.searchResultDescription}>{result.description}</div>}
          {result.date && <div className={styles.searchResultDate}>{result.date}</div>}
        </div>
      </div>
    </a>
  );
}

export function RSSFeedSearchModalElement({
  open,
  setOpen,
  search,
  setSearch,
  results,
  isLoading,
  placeholder = 'Search episodes...',
}: RSSFeedSearchModalElementProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset selection when search query changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally reset selection when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Reset selection when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIndex(0);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (results.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            window.open(results[selectedIndex].url, '_blank', 'noopener,noreferrer');
          }
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          break;
      }
    },
    [results, selectedIndex, setOpen],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent className={cn(styles.dialogContent, 'top-[10%] sm:top-[10%] translate-y-0 sm:translate-y-0')}>
          <div className={styles.dialogContainer}>
            <div className={styles.searchInputContainer}>
              <MagnifyingGlass className="w-4 h-4 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                placeholder={placeholder}
                className={cn(styles.searchInput, 'focus:outline-none focus:ring-0')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>
            {(results.length > 0 || isLoading) && <hr className={styles.separator} />}
            <div className={styles.searchResultsContainer} role="listbox">
              {isLoading && (
                <div className={styles.loadingState}>
                  <span>Loading...</span>
                </div>
              )}
              {!isLoading && results.length === 0 && (
                <div className={styles.emptyState}>
                  <span>{search ? 'No episodes found' : 'No episodes available'}</span>
                </div>
              )}
              {!isLoading &&
                results.map((result, index) => (
                  <RSSFeedSearchResultItem
                    key={result.id}
                    result={result}
                    isSelected={index === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(index)}
                  />
                ))}
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}
